/**
 * Rails sublist component for individual horizontal scrolling rails.
 * Manages horizontal navigation within a single rail, focus indicators, transform animations,
 * and interaction with floating menus. Supports various display modes and rail types.
 */

import Addon from '../../../../../shared/classes/addon';
import AIIcon from '@components/atoms/icons/AI';
import AppEvent from '@shared/classes/appEvent';
import ArrowsSelector from './selectors/arrowsSelector';
import Container from '../../../atoms/container/railSubListContainer';
import Corner from '../../../../../shared/classes/corner';
import Detail from '@components/pages/detail';
import Direction from '../../../../../shared/types/enums/direction';
import EpgEvent from '../../../../../shared/classes/epgEvent';
import FastEvent from '../../../../../shared/classes/fastEvent';
import Focus from '../../../../../shared/types/enums/focus';
import FocusSelector from './selectors/focusSelector';
import Main from '../../../../../shared/types/enums/main';
import MainContext from '../../../../../shared/components/utils/context/mainContext';
import MediaProduction from '../../../../../shared/classes/mediaProduction';
import OpacityAnimation from '../../../utils/helpers/opacityAnimation';
import Rail from '../../../../../shared/classes/rail';
import RailSelector from './selectors/railSelector';
import RegularEvent from '../../../../../shared/classes/regularEvent';
import Routes from '../../../../../shared/types/enums/routes';
import ScreenUtils from '../../../../../shared/utils/screen';
import Title from '../../../atoms/text/railsSubListTitle';
import closeMenu from '../../../../../shared/actions/menu/closeMenu';
import goToContent from '../../../../../shared/actions/content/goToContent';
import useForceUpdate from '../../../../../shared/components/utils/hooks/useForceUpdate';
import useKeyboard from '../../../../../shared/components/utils/hooks/useKeyboard';
import useList from '../../../../../shared/components/utils/hooks/useList';
import useTransform from '../../../../../shared/components/utils/hooks/useTransform';
import MODIFICATION from '../../../../../shared/types/enums/modification';
import { Coordinates } from '@shared/utils/floatingMenu';
import { floatingMenuSelector } from '../../../../../shared/store/slices/floatingMenu.slice';
import { focusSelector } from '../../../../../shared/store/slices/focus.slice';
import { openFloatingMenu } from '../../../../../shared/store/thunks/floatingMenu.thunk';
import { routerSelector } from '../../../../../shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { useContext, useEffect, useRef } from 'react';
import {
  RailDisplayMode,
  RailType,
} from '../../../../../shared/types/enums/rail';
import {
  hoverElement,
  leaveElement,
} from '../../../../../shared/store/slices/magicRemote.slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../shared/store/hooks';

/**
 * Floating menu coordinate mappings for different rail display modes.
 * Determines where to position the floating menu based on rail type.
 */
const FLOATING_MENU_COORDINATES = {
  photo: Coordinates.MAIN,
  channelGuidePhoto: Coordinates.MAIN,
  character: Coordinates.MAIN,
  poster: Coordinates.POSTER_RAIL,
  progressPhoto: Coordinates.MAIN,
  square: Coordinates.SQUARE_RAIL,
  related: Coordinates.RELATED_RAIL,
};

const HEIGHT = {
  addonPhoto: 260,
  cleanPhoto: 260,
  channelGuidePhoto: 260,
  character: 260,
  filter: 70,
  mosaic: 480,
  photo: 260,
  poster: 360,
  square: 172,
  title: 58,
};

interface IOffsetRatio {
  [key: string]: { 1920: number; 1280: number };
}

const OFFSET: IOffsetRatio = {
  addonPhoto: { 1920: 362, 1280: 239.995 },
  cleanPhoto: { 1920: 362, 1280: 239.995 },
  channelGuidePhoto: { 1920: 362, 1280: 239.995 },
  character: { 1920: 216, 1280: 143.333333333 },
  filter: { 1920: 220, 1280: 147 },
  photo: { 1920: 362, 1280: 239.995 },
  poster: { 1920: 212, 1280: 141.333333333 },
  square: { 1920: 129, 1280: 85.328 },
  rankingPoster: { 1920: 212, 1280: 141.333333333 },
};

interface Props {
  listId: number;
  rail: Rail;
  modifyRails: (modificationType: MODIFICATION, listId: number) => void;
  onItemClick?: (
    horizontalIndex: number,
    verticalIndex: number,
    focus: number | (string | number)[] | number[] | null
  ) => void;
}

export default function RailsSubList({
  listId,
  rail,
  modifyRails,
  onItemClick,
}: Props) {
  const dispatch = useAppDispatch();
  const { data, displayMode, dummy, fixed, reelIA, title, type } = rail;
  const { focus } = useAppSelector(focusSelector);
  const { open } = useAppSelector(floatingMenuSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const { rails, setAsset, onChangeElement, outOfBoundsAction } =
    useContext(MainContext);
  const { distanceToIndex, listIndex } = useList(listId);

  // Misc data

  const listFocus = parseInt(`${Focus.RAILS_SUB_LIST}${listId}`);
  const menuFocused = focus === Focus.MENU || focus === Focus.LOGIN;
  const previousParams = currentParams as {
    previousFocus: number[];
    horizontalIndex: number;
  };
  const currentRail =
    rails?.length && rails.length > listId ? rails[listId - 1] : null;

  const initialIndex = previousParams?.previousFocus?.length
    ? previousParams?.previousFocus.includes(listFocus)
      ? Math.min(previousParams?.horizontalIndex || 0, data.length - 1)
      : 0
    : 0;
  const isFocused =
    ((focus as number[]).length && (focus as number[]).includes(listFocus)) ||
    focus === listFocus;
  const isFilter = type === RailType.FILTER;
  const isMosaic = type === RailType.MOSAIC;

  // References

  const dataRef = useRef(data);
  const indexRef = useRef(initialIndex);
  const isFocusedRef = useRef<boolean | number>(false);
  const railRef = useRef(rail);
  const setSubListIndex = useRef<
    (newIndex: number, increment?: number) => void
  >(() => null);
  const typeRef = useRef<string>(displayMode);
  railRef.current = rail;
  isFocusedRef.current = isFocused;
  typeRef.current = displayMode;

  // Actions

  const updateData = (
    newData: (
      | Addon
      | AppEvent
      | Corner
      | EpgEvent
      | FastEvent
      | MediaProduction
      | undefined
    )[],
    resetStyle?: boolean
  ) => {
    dataRef.current = newData;
    if (resetStyle) reset();
  };

  // Events handlers

  const onClick = (
    item: Addon | Corner | EpgEvent | RegularEvent | undefined,
    horizontalIndex: number,
    listFocus: number
  ) => {
    dispatch(leaveElement());
    goToContent(item);
    onItemClick &&
      onItemClick(horizontalIndex, listId, [Focus.RAILS_LIST, listFocus]);
  };

  const onChange = (index: number, increment?: number) => {
    indexRef.current = index;
    setTimeout(() => {
      if (increment === -1 || increment === 1)
        setStyle(
          increment,
          OFFSET[typeRef.current as RailDisplayMode.PHOTO][
            ScreenUtils.width as 1920
          ] ||
            // @ts-ignore
            OFFSET[typeRef.current as RailDisplayMode.PHOTO]['default'] ||
            OFFSET[typeRef.current as RailDisplayMode.PHOTO]
        );
    });
    setAsset(
      (
        dataRef.current as (
          | EpgEvent
          | FastEvent
          | MediaProduction
          | undefined
        )[]
      )[index]
    );
    onChangeElement(Main.RAILS, index);
  };

  const onMouseEnter = () => {
    if (!open) {
      closeMenu();
      if (!dummy && rail.type !== RailType.FILTER) {
        dispatch(hoverElement());
      }
    }
  };

  const onMouseLeave = () => {
    dispatch(leaveElement());
  };

  // Update hook

  const forceUpdate = useForceUpdate();

  // Effect hooks

  useEffect(() => {
    dataRef.current = data as (
      | EpgEvent
      | FastEvent
      | MediaProduction
      | Corner
      | undefined
    )[];
    forceUpdate();
  }, [data]);

  useEffect(() => {
    if (isFocused) {
      setAsset(
        (
          dataRef.current as (
            | EpgEvent
            | FastEvent
            | MediaProduction
            | undefined
          )[]
        )[indexRef.current]
      );
    }
  }, [dataRef, isFocused]);

  // Keyboard hook

  useKeyboard({
    canBeFocused: false,

    keyDownHandler: () => null,
    keyPressHandler: () => {
      const { router } = store.getState();
      if (isFocusedRef.current) {
        dispatch(
          openFloatingMenu({
            route: Routes.MAIN,
            asset: (
              dataRef.current as (
                | EpgEvent
                | FastEvent
                | MediaProduction
                | undefined
              )[]
            )[indexRef.current],
            position:
              railRef.current.displayMode === 'photo' &&
              router.route.Page === Detail
                ? FLOATING_MENU_COORDINATES['related']
                : FLOATING_MENU_COORDINATES[
                    railRef.current.displayMode as RailDisplayMode.PHOTO
                  ],
            type: railRef.current.type,
          })
        );
      }
    },
  });

  // Transform hook

  const [transformStyle, setStyle, reset] = useTransform(
    indexRef.current,
    OFFSET[typeRef.current as RailDisplayMode.PHOTO][ScreenUtils.width as 1920],
    Direction.HORIZONTAL,
    false
  );

  // List data

  const list = {
    data: dataRef.current,
    ogData: data,
    displayMode,
    fixed,
    focus: listFocus,
    index: indexRef.current,
    id: listId,
    deleteRail: () => modifyRails(MODIFICATION.DELETE, listId),
    onChange,
    onClick,
    outOfBounds: outOfBoundsAction,
    resetStyle: reset,
    setListIndex: setSubListIndex,
    updateData,
  };

  // Styles

  const railAnimation = { transition: 'transform 0.15s' };
  const railHeight =
    HEIGHT[isMosaic ? 'mosaic' : (displayMode as RailDisplayMode.PHOTO)] -
    (!title && !isFilter ? HEIGHT['title'] : 0);
  const railStyle = {
    ...(!isMosaic && !fixed
      ? transformStyle
      : { overflow: isMosaic ? 'hidden' : 'unset' }),
  };
  const displayFocus = (isFocused || false) && !isMosaic && !fixed;
  const displayOpacity =
    !isFocused && currentRail?.type === RailType.MOSAIC
      ? (currentParams?.displayNextRails as boolean) ?? listIndex === listId
      : true;
  const displayRail =
    isMosaic ||
    (distanceToIndex > (isFilter ? -2 : -1) &&
      distanceToIndex <
        (!!rails?.find((item) => item.displayMode === RailDisplayMode.FILTER)
          ? 5
          : 4));
  return (
    <Container
      allowPointerEvents={displayRail}
      canOverflow={!isMosaic && !isFilter && menuFocused}
      height={railHeight}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {displayRail && (
        <OpacityAnimation display={displayOpacity}>
          {title ? (
            <Title>
              {reelIA ? (
                <AIIcon padding={`0 ${6 * ScreenUtils.widthRatio}px 0 0`} />
              ) : null}
              {title}
            </Title>
          ) : null}
          <RailSelector
            animation={railAnimation}
            style={railStyle}
            list={list}
            type={type}
          />
          <FocusSelector displayMode={displayMode} display={displayFocus} />
          <ArrowsSelector
            displayMode={displayMode}
            setListIndex={setSubListIndex}
          />
        </OpacityAnimation>
      )}
    </Container>
  );
}
